import { useLayoutEffect, useMemo } from 'react';
import { AdvancedFilterType, Row } from 'talent_matching/types';
import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import {
  AdvancedFilterParams,
  CompanyNameFilter,
  Person,
  TalentMatchingFilters,
  TalentMatchingQueryParams,
} from 'talent_matching/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { SCORECARD_STATUS } from 'talent_search/shared/types';

export const advancedFilterParams = (filters: AdvancedFilterType[]) => {
  return filters.reduce(
    (obj: AdvancedFilterParams, filter: AdvancedFilterType) => {
      const advancedFilter = new AdvancedFilter(filter);
      const key = advancedFilter.queryKey();
      const value = advancedFilter.queryParamValue();

      if (!(key && value)) {
        return obj;
      }

      if (key.startsWith('defined_cf_')) {
        const typedKey = key as `defined_cf_${string}`;
        obj[typedKey] = value as '1' | '0';
      } else {
        const existingValue = obj[key] || [];
        const newValue = Array.isArray(value) ? value : [value];
        // Non-custom-field keys (e.g., source_id, degree_id, etc.)
        const typedKey = key as keyof Omit<
          AdvancedFilterParams,
          `defined_cf_${string}` | `defined_unlinked_q_${string}`
        >;
        obj[typedKey] = [...existingValue, ...newValue];
      }

      return obj;
    },
    {}
  );
};

export const questionFiltersParams = (
  questionFilters: AdvancedFilterType[]
) => {
  return questionFilters.reduce((obj: AdvancedFilterParams, row) => {
    const advancedFilter = new AdvancedFilter(row);

    const key = advancedFilter.queryKey();
    const value = advancedFilter.queryParamValue();

    if (!(key && value)) {
      return obj;
    }

    if (key.startsWith('defined_cf_')) {
      const typedKey = key as `defined_cf_${string}`;
      obj[typedKey] = value as '1' | '0';
      return obj;
    }

    if (key.startsWith('defined_unlinked_q_')) {
      const typedKey = key as `defined_unlinked_q_${string}`;
      obj[typedKey] = value as '1' | '0';
      return obj;
    }

    const existingValue = obj[key] || [];
    const newValue = Array.isArray(value) ? value : [value];
    const typedKey = key as keyof Omit<
      AdvancedFilterParams,
      `defined_cf_${string}` | `defined_unlinked_q_${string}`
    >;
    obj[typedKey] = [...existingValue, ...newValue];
    return obj;
  }, {});
};

const companyNameParams = (filters: CompanyNameFilter[]) => {
  return filters.reduce(
    (
      obj: {
        company_name_includes?: string[];
        company_name_excludes?: string[];
      },
      filter: CompanyNameFilter
    ) => {
      const mode = filter.mode;
      const value = filter.value;

      if (!(mode && value)) {
        return obj;
      }

      const key =
        mode === 'include' ? 'company_name_includes' : 'company_name_excludes';

      obj[key] = [...(obj[key] || []), value];
      return obj;
    },
    {}
  );
};

const locationParams = (filters: TalentMatchingFilters) => {
  const {
    locationFilterType,
    debouncedLocationName,
    geolocation,
    geolocationRadius,
  } = filters;

  if (locationFilterType === 'name' && debouncedLocationName) {
    return {
      location_name: debouncedLocationName,
    };
  }

  if (locationFilterType === 'radius' && geolocation && geolocationRadius) {
    return {
      geolocation: geolocation,
      geolocation_radius: geolocationRadius,
    };
  }

  return {};
};

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return true;
};

const hasAppliedFilters = (
  filterParams: TalentMatchingQueryParams['filterParams']
): boolean => {
  const {
    exclude_application_ids: _excludedIds,
    scorecard_status,
    ...rest
  } = filterParams;

  return (
    scorecard_status !== SCORECARD_STATUS.ALL ||
    Object.values(rest).some(hasMeaningfulValue)
  );
};

const useQueryParams = (): TalentMatchingQueryParams => {
  const { control } = useFormContext<TalentMatchingFilters>();
  const formValues = useWatch({ control }) as TalentMatchingFilters;
  const combinedAdvancedFilters = [
    ...(formValues.advancedFilters || []),
    ...(formValues.educationFilters || []),
  ];

  const filterParams = {
    application_created_at: formValues.appliedOnStart,
    application_created_end: formValues.appliedOnEnd,
    job_post_id: formValues.jobPosts?.map((item) => item.value.toString()),
    exclude_application_ids: formValues.excludedIds,
    manual_review: formValues.manualReview,
    exclude_previously_rejected: formValues.excludePreviouslyRejected,
    priority_candidates: formValues.priorityCandidates,
    without_potential_fraud: formValues.excludeHighRisk,
    saved_for_later: formValues.savedForLater,
    bookmarked_by_user_id: formValues.bookmarkedByTeammates?.map((u) =>
      u.value.toString()
    ),
    other_active_applications: formValues.otherActiveApplications,
    scorecard_status: formValues.feedbackStatus,
    years_of_experience_min: formValues.minExperience,
    years_of_experience_max: formValues.maxExperience,
    can_have: formValues.canHave,
    must_have: formValues.mustHave,
    does_not_have: formValues.doesNotHave,
    ...advancedFilterParams(combinedAdvancedFilters),
    ...questionFiltersParams(formValues.questionFilters),
    ...companyNameParams(formValues.companyNameFilters),
    ...locationParams(formValues),
  };

  return {
    queryParams: {
      page: formValues.page,
      page_size: formValues.perPage,
      ...filterParams,
    },
    filterParams,
  };
};

export const useFiltersApplied = (): boolean => {
  const { filterParams } = useQueryParams();

  return useMemo(() => hasAppliedFilters(filterParams), [filterParams]);
};

// This holds the entire set of people and is only used to dictate row order and
// populate the Person Cache. This should only be used once in the table
// component.
export const usePeople = (): UseQueryResult<{
  rows: Row[];
  totalRows: number;
}> & {
  queryKey: string[];
} => {
  const { paths } = useTalentMatchingTabContext();
  const queryClient = useQueryClient();
  const { queryParams: params, filterParams } = useQueryParams();

  const pathWithSearch = paths.peoplePath + window.location.search;
  const queryString = JSON.stringify(params);
  const filterQueryString = JSON.stringify(filterParams);
  const currentQueryKey = ['people', queryString];

  useLayoutEffect(() => {
    // We want to dump the cache here because we've changed filters
    queryClient.invalidateQueries({
      queryKey: ['people'],
      refetchType: 'none',
    });
  }, [filterQueryString, queryClient]);

  const queryResult = useQuery({
    queryKey: currentQueryKey,
    queryFn: async () => {
      const { data } = await standardRequest.get(pathWithSearch, {
        params,
      });

      const rows: Row[] = data.applications.map((person: Person) => {
        queryClient.setQueryData(['person', person.id], person);
        return { id: person.id };
      });

      return { rows, totalRows: data.total };
    },
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  return {
    ...queryResult,
    queryKey: currentQueryKey,
  };
};

// Individual Person hook for accessing Person records in the cache.
// This is the source of truth for any UI related to a single Person (rows, panel, etc.)
// The side panel and rows are already calling this, so there should be no need
// to use this unless we're adding something that lives outside of a row or the
// side panel.
export const usePerson = (
  personId: number,
  cacheKey?: string
): { person: Person; updatePerson: (p: Person) => void } => {
  const queryClient = useQueryClient();
  const key = cacheKey || 'person';

  const query = useQuery({
    queryKey: [key, personId],
    staleTime: Infinity,
    queryFn: () => queryClient.getQueryData([key, personId]) as Person,
    retry: 1,
  });

  return {
    person: query.data as Person,
    // This is for convenience so components don't have to import useUpdatePerson separately
    updatePerson: useUpdatePerson(key),
  };
};

// Use this whenever you want to update a Person in the cache. There is no need to invalidate
// any queries since we're updating the cache directly.
export const useUpdatePerson = (cacheKey = 'person') => {
  const queryClient = useQueryClient();

  return (updatedPerson: Person) => {
    queryClient.setQueryData([cacheKey, updatedPerson.id], updatedPerson);
  };
};

export const useGetPerson = () => {
  const queryClient = useQueryClient();

  return (personId: number): Person | undefined => {
    return queryClient.getQueryData<Person>(['person', personId]);
  };
};
